/**
 * Credit Engine Service (credit-engine-business)
 * API Gateway path: /credit
 * Backoffice: dashboard, config/policies, CRUD, financial profiles
 */

import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/axios';
import type {
  CreditDashboardStats,
  NurturingFunnelItem,
  OriginationWeeklyItem,
} from '@/types/credit.types';
import type { ActivePortfolioCredit } from '@/types/portfolio.types';
import type {
  BackofficeCreditsListItem,
  BackofficeCreditDetail,
  BackofficeApplicationsListItem,
  BackofficeApplicationDetail,
  BackofficeOffersListItem,
  BackofficeOfferDetail,
  FinancialProfileEntity,
} from '@/types/backoffice-crud.types';
import type {
  PaginatedProfilesResponse,
  ProfilesListFilters,
  ProfileSummaryResponse,
  ClientIncomeSourcesResponse,
} from '@/types/scoring.types';
import type { CollectionRecord } from '@/types/collection.types';
import type {
  AnticipatedPaymentRequest,
  AdvancedPaymentRequest,
  SettlementPaymentRequest,
  EarlyPaymentResponse,
  SettlementQuoteResponse,
} from '@/types/early-payment.types';
import type {
  EligibilityRuleItem,
  CreditLevelItem,
  InterestRateItem,
  SplitConfigItem,
  ExposureLimitItem,
  SimulatorRequest,
  SimulatorResponse,
} from '@/types/policy.types';

const BASE = '/credit';
// RBAC v2: los endpoints backoffice viven bajo /credit/v2/backoffice (W4 + @RequirePermission).
// Convención confirmada: path v2 = insertar /v2 después del basepath del lambda.
const BACKOFFICE = `${BASE}/v2/backoffice`;
// OJO: movements y early-payment (más abajo) usan `${BASE}/${partnerId}/...` y NO tienen
// gemelo /v2 en el openapi de credit-engine → siguen en legacy hasta decisión de backend.

// ─── Dashboard ───────────────────────────────────────────────────────────────

export const getDashboardSummary = (partnerId: string) =>
  apiGet<CreditDashboardStats>(`${BACKOFFICE}/dashboard/summary`, { params: { partnerId } });

export const getOriginationWeekly = (partnerId: string) =>
  apiGet<OriginationWeeklyItem[]>(`${BACKOFFICE}/dashboard/origination/weekly`, { params: { partnerId } });

export const getNurturingFunnel = (partnerId: string) =>
  apiGet<NurturingFunnelItem[]>(`${BACKOFFICE}/dashboard/nurturing/funnel`, { params: { partnerId } });

export const getPortfolioActive = (partnerId: string) =>
  apiGet<ActivePortfolioCredit[]>(`${BACKOFFICE}/dashboard/portfolio/active`, { params: { partnerId } });

export const getPortfolioBalance = (partnerId: string) =>
  apiGet<any>(`${BACKOFFICE}/dashboard/portfolio/balance`, { params: { partnerId } });

export const getPortfolioByLevel = (partnerId: string) =>
  apiGet<any[]>(`${BACKOFFICE}/dashboard/portfolio/by-level`, { params: { partnerId } });

export const getOriginationMonthly = (partnerId: string, months = 6) =>
  apiGet<any[]>(`${BACKOFFICE}/dashboard/origination/monthly`, { params: { partnerId, months } });

export const getFunnelV2 = (partnerId: string) =>
  apiGet<any[]>(`${BACKOFFICE}/dashboard/funnel`, { params: { partnerId } });

// ─── Dashboard V3 ───────────────────────────────────────────────────────────

export const getPipelineMatrix = (partnerId: string, weeks = 20) =>
  apiGet<any[]>(`${BACKOFFICE}/dashboard/pipeline-matrix`, { params: { partnerId, weeks } });

export const getOriginationSummary = (partnerId: string, month?: string) =>
  apiGet<any>(`${BACKOFFICE}/dashboard/origination/summary`, { params: { partnerId, ...(month && { month }) } });

export const getOriginationEfficiency = (partnerId: string) =>
  apiGet<any>(`${BACKOFFICE}/dashboard/origination/efficiency`, { params: { partnerId } });

export const getOriginationComposition = (partnerId: string, month?: string) =>
  apiGet<any>(`${BACKOFFICE}/dashboard/origination/composition`, { params: { partnerId, ...(month && { month }) } });

export const getCohortQuality = (partnerId: string, months = 6) =>
  apiGet<any[]>(`${BACKOFFICE}/dashboard/origination/cohort-quality`, { params: { partnerId, months } });

export const getPortfolioUpcoming = (partnerId: string, weeks = 12) =>
  apiGet<any[]>(`${BACKOFFICE}/dashboard/portfolio/upcoming`, { params: { partnerId, weeks } });

export const getPortfolioBalanceMonthly = (partnerId: string, months = 12) =>
  apiGet<any[]>(`${BACKOFFICE}/dashboard/portfolio/balance/monthly`, { params: { partnerId, months } });

export const getPortfolioTimeline = (partnerId: string, months = 12) =>
  apiGet<any[]>(`${BACKOFFICE}/dashboard/portfolio/timeline`, { params: { partnerId, months } });

export const getPortfolioResolution = (partnerId: string, months = 12) =>
  apiGet<any[]>(`${BACKOFFICE}/dashboard/portfolio/resolution`, { params: { partnerId, months } });

// ─── Config Auditable (P9) ──────────────────────────────────────────────────

export const getConfigSnapshots = (partnerId: string) =>
  apiGet<any[]>(`${BACKOFFICE}/config/snapshots`, { params: { partnerId } });

export const getConfigSnapshotDetail = (partnerId: string, version: number) =>
  apiGet<any>(`${BACKOFFICE}/config/snapshots/${version}`, { params: { partnerId } });

export const simulateConfigImpact = (partnerId: string, configType: string, proposedConfig: any[]) =>
  apiPost<any>(`${BACKOFFICE}/config/simulate-impact`, { partnerId, configType, proposedConfig });

// ─── Policies / Config ───────────────────────────────────────────────────────

export const getConfig = (configType: string, partnerId: string) =>
  apiGet<unknown>(`${BACKOFFICE}/config/${configType}`, { params: { partnerId } });

export const getScoreConfig = (partnerId: string) =>
  apiGet<any[]>(`${BACKOFFICE}/config/scores`, { params: { partnerId } });

export const updateConfig = (configType: string, data: unknown) =>
  apiPut<unknown>(`${BACKOFFICE}/config/${configType}`, data);

export const getEligibilityRules = (partnerId: string) =>
  apiGet<EligibilityRuleItem[]>(`${BACKOFFICE}/config/eligibility-rules`, { params: { partnerId } });

export const updateEligibilityRules = (data: { partnerId: string; items: EligibilityRuleItem[] }) =>
  apiPut<EligibilityRuleItem[]>(`${BACKOFFICE}/config/eligibility-rules`, data);

export const getCreditLevelConfig = (partnerId: string) =>
  apiGet<CreditLevelItem[]>(`${BACKOFFICE}/config/credit-levels`, { params: { partnerId } });

export const updateCreditLevelConfig = (data: { partnerId: string; items: CreditLevelItem[] }) =>
  apiPut<CreditLevelItem[]>(`${BACKOFFICE}/config/credit-levels`, data);

export const getInterestRateConfig = (partnerId: string) =>
  apiGet<InterestRateItem[]>(`${BACKOFFICE}/config/interest-rates`, { params: { partnerId } });

export const updateInterestRateConfig = (data: { partnerId: string; items: InterestRateItem[] }) =>
  apiPut<InterestRateItem[]>(`${BACKOFFICE}/config/interest-rates`, data);

export const getSplitConfig = (partnerId: string) =>
  apiGet<SplitConfigItem[]>(`${BACKOFFICE}/config/split`, { params: { partnerId } });

export const updateSplitConfig = (data: { partnerId: string; items: SplitConfigItem[] }) =>
  apiPut<SplitConfigItem[]>(`${BACKOFFICE}/config/split`, data);

export const getExposureLimits = (partnerId: string) =>
  apiGet<ExposureLimitItem[]>(`${BACKOFFICE}/config/exposure-limits`, { params: { partnerId } });

export const updateExposureLimits = (data: { partnerId: string; items: ExposureLimitItem[] }) =>
  apiPut<ExposureLimitItem[]>(`${BACKOFFICE}/config/exposure-limits`, data);

// ─── Simulator ───────────────────────────────────────────────────────────────

export const simulateCredit = (data: SimulatorRequest) =>
  apiPost<SimulatorResponse>(`${BACKOFFICE}/config/simulate`, data);

// ─── CRUD: Credits ──────────────────────────────────────────────────────────

export const listCredits = (partnerId: string) =>
  apiGet<BackofficeCreditsListItem[]>(`${BACKOFFICE}/credits`, { params: { partnerId } });

export const getCreditDetail = (creditId: string, directoryId: string) =>
  apiGet<BackofficeCreditDetail>(`${BACKOFFICE}/credits/${creditId}`, { params: { directoryId } });

// ─── CRUD: Applications ─────────────────────────────────────────────────────

export const listApplications = (partnerId: string) =>
  apiGet<BackofficeApplicationsListItem[]>(`${BACKOFFICE}/applications`, { params: { partnerId } });

export const getApplicationDetail = (applicationId: string, directoryId: string) =>
  apiGet<BackofficeApplicationDetail>(`${BACKOFFICE}/applications/${applicationId}`, { params: { directoryId } });

// ─── CRUD: Offers ───────────────────────────────────────────────────────────

export const listOffers = (partnerId: string) =>
  apiGet<BackofficeOffersListItem[]>(`${BACKOFFICE}/offers`, { params: { partnerId } });

export const getOfferDetail = (offerId: string, directoryId: string) =>
  apiGet<BackofficeOfferDetail>(`${BACKOFFICE}/offers/${offerId}`, { params: { directoryId } });

// ─── CRUD: Financial Profile ────────────────────────────────────────────────

export const getFinancialProfile = (directoryId: string, partnerId: string) =>
  apiGet<FinancialProfileEntity>(`${BACKOFFICE}/profiles/${directoryId}`, { params: { partnerId } });

// ─── Profiles & Income Sources ─────────────────────────────────────────────

export const listProfiles = (partnerId: string, filters?: ProfilesListFilters) =>
  apiGet<PaginatedProfilesResponse>(`${BACKOFFICE}/profiles`, {
    params: { partnerId, ...filters },
  });

export const getProfilesSummary = (partnerId: string) =>
  apiGet<ProfileSummaryResponse>(`${BACKOFFICE}/profiles/summary`, { params: { partnerId } });

export const getIncomeSources = (directoryId: string, partnerId: string) =>
  apiGet<ClientIncomeSourcesResponse>(
    `${BACKOFFICE}/profiles/${directoryId}/income-sources`,
    { params: { partnerId } },
  );

// ─── Monthly Statements (P7) ────────────────────────────────────────────────

import type {
  CreditStatementsListResponse,
  CreditStatementPdfResponse,
} from '@/types/credit.types';

/** GET /backoffice/credits/{creditId}/statements -- Lista de estados de cuenta */
export const getCreditStatements = (creditId: string) =>
  apiGet<CreditStatementsListResponse>(`${BACKOFFICE}/credits/${creditId}/statements`);

/** GET /backoffice/statements/{statementId}/pdf -- URL pre-firmada para descargar el PDF */
export const getCreditStatementPdf = (statementId: string) =>
  apiGet<CreditStatementPdfResponse>(`${BACKOFFICE}/statements/${statementId}/pdf`);

// ─── Signed Contracts ───────────────────────────────────────────────────────

import type {
  SignedContractsListResponse,
  SignedContractPdfResponse,
} from '@/types/credit.types';

/** GET /backoffice/directories/{directoryId}/signed-contracts -- Lista de contratos firmados del usuario */
export const getSignedContracts = (directoryId: string) =>
  apiGet<SignedContractsListResponse>(`${BACKOFFICE}/directories/${directoryId}/signed-contracts`);

/** GET /backoffice/contracts/{contractId}/pdf -- URL pre-firmada para visualizar el PDF inline */
export const getSignedContractPdf = (contractId: string, partnerId: string) =>
  apiGet<SignedContractPdfResponse>(`${BACKOFFICE}/contracts/${contractId}/pdf`, {
    params: { partnerId, category: 'CREDITS' },
  });

// ─── Movements (CollectionRecords per credit) ──────────────────────────────

/** GET /{partnerId}/credit/{creditId}/movements -- Historial de pagos del credito */
export const getCreditMovements = (partnerId: string, creditId: string) =>
  apiGet<CollectionRecord[]>(`${BASE}/${partnerId}/credit/${creditId}/movements`);

// ─── Early Payment ──────────────────────────────────────────────────────────

/** POST /{partnerId}/early-payment/anticipated -- Abono a capital */
export const applyAnticipatedPayment = (partnerId: string, data: AnticipatedPaymentRequest) =>
  apiPost<EarlyPaymentResponse>(`${BASE}/${partnerId}/early-payment/anticipated`, data);

/** POST /{partnerId}/early-payment/advanced -- Pago adelantado de cupones */
export const applyAdvancedPayment = (partnerId: string, data: AdvancedPaymentRequest) =>
  apiPost<EarlyPaymentResponse>(`${BASE}/${partnerId}/early-payment/advanced`, data);

/** GET /{partnerId}/early-payment/settlement-quote/{creditId} -- Cotizacion de liquidacion */
export const getSettlementQuote = (partnerId: string, creditId: string) =>
  apiGet<SettlementQuoteResponse>(`${BASE}/${partnerId}/early-payment/settlement-quote/${creditId}`);

/** POST /{partnerId}/early-payment/settlement -- Liquidacion total */
export const applySettlementPayment = (partnerId: string, data: SettlementPaymentRequest) =>
  apiPost<EarlyPaymentResponse>(`${BASE}/${partnerId}/early-payment/settlement`, data);

// ─── Whitelist de pruebas productivas ──────────────────────────────────────
// Feature flag WHITELIST_ENABLED controlado en backend; los endpoints responden
// independientemente del flag — el flag solo afecta el motor de credito.

import type {
  WhitelistItem,
  AddWhitelistRequest,
  AddWhitelistResponse,
  SearchClientsForWhitelistItem,
} from '@/types/whitelist.types';

/** GET /backoffice/whitelist -- Lista de clientes en whitelist */
export const listWhitelist = (partnerId: string, includeInactive = false) =>
  apiGet<WhitelistItem[]>(`${BACKOFFICE}/whitelist`, {
    params: { partnerId, ...(includeInactive ? { include_inactive: 'true' } : {}) },
  });

/** POST /backoffice/whitelist -- Agrega un cliente a la whitelist */
export const addToWhitelist = (partnerId: string, body: AddWhitelistRequest, addedBy?: string) =>
  apiPost<AddWhitelistResponse>(`${BACKOFFICE}/whitelist`, body, {
    params: { partnerId, ...(addedBy ? { addedBy } : {}) },
  });

/** DELETE /backoffice/whitelist/:directoryId -- Remueve un cliente de la whitelist */
export const removeFromWhitelist = (partnerId: string, directoryId: string, removedBy?: string) =>
  apiDelete<{ removed: true }>(`${BACKOFFICE}/whitelist/${encodeURIComponent(directoryId)}`, {
    params: { partnerId, ...(removedBy ? { removedBy } : {}) },
  });

/** GET /backoffice/whitelist/search-clients -- Busca clientes para agregar a la whitelist */
export const searchClientsForWhitelist = (partnerId: string, q: string) =>
  apiGet<SearchClientsForWhitelistItem[]>(`${BACKOFFICE}/whitelist/search-clients`, {
    params: { partnerId, q },
  });
